import React, { useEffect, useState } from 'react';
import { getUsers, getExpected, createExpected, deleteExpected } from '../../api';
import { CustomerSearch } from '../../components/CustomerSearch';

export default function RegisterCodes() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expectedList, setExpectedList] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    getUsers({ role: 'customer' }).then(setCustomers).catch(console.error);
  }, []);

  const loadExpected = async (customerId) => {
    if (!customerId) { setExpectedList([]); return; }
    setListLoading(true);
    try {
      setExpectedList(await getExpected({ customer_id: customerId }));
    } catch(e) { console.error(e); }
    finally { setListLoading(false); }
  };

  const handleCustomerChange = (id) => {
    setSelectedCustomer(id);
    setResults(null);
    loadExpected(id);
  };

  const handleRegister = async () => {
    const codes = input.trim().split(/\s+/).filter(Boolean);
    if (!codes.length) return;
    if (!selectedCustomer) { alert('Please pick a customer!'); return; }
    if (!warehouse) { alert('Please pick a warehouse!'); return; }
    setLoading(true);
    try {
      // Temporary override of user context by sending customer_id in body
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/expected/staff-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('kdx_token')}` },
        body: JSON.stringify({ codes, warehouse, customer_id: selectedCustomer })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Server error');
      setResults(data);
      setInput('');
      loadExpected(selectedCustomer);
    } catch(e) { alert(e.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    await deleteExpected(id);
    loadExpected(selectedCustomer);
  };

  const statusColor = (s) => s==='received'?'var(--color-success)':s==='duplicate'?'var(--color-warning)':'var(--text-muted)';
  const statusLabel = (s) => s==='received'?'✅ Arrived':s==='duplicate'?'⚠ Already registered':s==='claimed'?'❌ Claimed by another customer':'⏳ Waiting';

  return (
    <div>
      <style>{`
        .ci:focus{outline:none;border-color:var(--accent)!important;box-shadow:0 0 0 3px var(--accent-bg)!important;}
        .add-btn:hover{background:var(--bg-elevated)!important;border-color:var(--accent)!important;}
        .del-btn{background:transparent;border:1px solid rgba(220,38,38,0.20);color:rgba(220,38,38,0.40);border-radius:5px;padding:3px 8px;font-size:11px;cursor:pointer;}
        .del-btn:hover{background:rgba(220,38,38,0.08);color:#b91c1c;border-color:rgba(220,38,38,0.35);}
        .fs{background:var(--bg-tertiary);border:1px solid var(--border);border-radius:var(--radius-md);padding:9px 12px;font-size:13px;color:var(--text-primary);outline:none;width:100%;}
        .fs:focus{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-bg);}
      `}</style>

      <div style={{marginBottom:24}}>
        <div className="page-eyebrow">MANAGEMENT</div>
        <h1 className="page-title">Register Codes</h1>
        <p className="page-subtitle">Staff register tracking on behalf of customers before arrival</p>
      </div>

      {/* Form */}
      <div style={s.card}>
        <div style={s.cardTitle}>REGISTER NEW CODES</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div>
            <label style={s.label}>CUSTOMER</label>
            <CustomerSearch
              customers={customers}
              value={selectedCustomer}
              onChange={handleCustomerChange}
              placeholder="Search code or customer name..."
              style={{width:'100%'}}
            />
          </div>
          <div>
            <label style={s.label}>WAREHOUSE</label>
            <select className="ci" style={s.input} value={warehouse} onChange={e=>setWarehouse(e.target.value)}>
              <option value="">-- Pick warehouse --</option>
              <option value="Kho 1">Kho 4450 Van</option>
              <option value="Kho 2">Kho 7320 Cal</option>
              <option value="Kho 3">Kho 3838/135 Cal</option>
              <option value="Kho 4">Kho 465 Toronto</option>
              <option value="Kho Surrey">Kho Surrey</option>
              <option value="ARBOURRIDGE">ARBOURRIDGE (Cal)</option>
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={s.label}>TRACKING / CUSTOMER ORDER</label>
          <textarea className="ci" rows={4}
            placeholder="Paste multiple codes, separated by spaces or newlines..."
            value={input} onChange={e=>setInput(e.target.value)}
            style={{...s.input,resize:'vertical',fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,width:'100%'}}
          />
        </div>
        <button className="add-btn" onClick={handleRegister} disabled={loading||!input.trim()||!selectedCustomer||!warehouse}
          style={{background:'var(--bg-tertiary)',color:'var(--text-primary)',border:'1px solid var(--accent-strong)',borderRadius:8,padding:'9px 20px',fontSize:13,fontWeight:600,cursor:'pointer',opacity:(!input.trim()||!selectedCustomer||!warehouse)?0.45:1}}>
          {loading?'Registering...':'📋 Register'}
        </button>
      </div>

      {/* Result */}
      {results && (
        <div style={{...s.card,marginBottom:16}}>
          <div style={s.cardTitle}>REGISTRATION RESULT</div>
          {results.map((r,i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px',marginBottom:4,
              background:'var(--bg-tertiary)',border:'1px solid rgba(15,23,42,0.06)',borderRadius:8}}>
              <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:13,fontWeight:700,color:'var(--text-primary)'}}>{r.code}</div>
              <div style={{fontSize:11,fontWeight:600,color:statusColor(r.status)}}>{statusLabel(r.status)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Registered list */}
      {selectedCustomer && (
        <div style={s.card}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
            <div style={s.cardTitle}>REGISTERED CODES ({expectedList.length})</div>
            <div style={{fontSize:12,color:'var(--text-muted)'}}>
              {customers.find(c=>c.id===parseInt(selectedCustomer))?.code} — {customers.find(c=>c.id===parseInt(selectedCustomer))?.name}
            </div>
          </div>
          {listLoading ? (
            <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:20}}>Loading...</div>
          ) : expectedList.length === 0 ? (
            <div style={{textAlign:'center',color:'var(--text-muted)',fontSize:13,padding:20}}>No codes yet</div>
          ) : expectedList.map(e=>(
            <div key={e.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',marginBottom:6,
              background: e.status==='received'?'rgba(22,163,74,0.08)':'var(--bg-secondary)',
              border:`1px solid ${e.status==='received'?'rgba(22,163,74,0.22)':'var(--border)'}`,
              borderRadius:8}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontVariantNumeric:"tabular-nums",fontWeight:600,fontSize:12,fontWeight:700,color:'var(--text-primary)',marginBottom:2}}>{e.code}</div>
                <div style={{fontSize:11,color:'var(--text-muted)'}}>{e.warehouse} · {new Date(e.created_at).toLocaleDateString('vi-VN')}</div>
              </div>
              <span style={{fontSize:11,fontWeight:600,color:e.status==='received'?'var(--color-success)':'var(--text-muted)',flexShrink:0}}>
                {e.status==='received'?'✅ Arrived':'⏳ Waiting'}
              </span>
              {e.status !== 'received' && (
                <button className="del-btn" onClick={()=>handleDelete(e.id)}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  card: { background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.07)', borderRadius:12, padding:'18px 20px', marginBottom:20 },
  cardTitle: { fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1.8, marginBottom:14 },
  label: { display:'block', fontSize:10, fontWeight:700, color:'var(--text-muted)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 },
  input: { width:'100%', background:'var(--bg-tertiary)', border:'1px solid rgba(15,23,42,0.08)', borderRadius:8, padding:'9px 12px', fontSize:13, color:'var(--text-primary)',  },
};
